'use client';

import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { useLogging } from 'src/context/logging';
import { useNotification } from 'src/context/notification';
import { useDashboard } from 'src/context/providers/DashboardProvider';
import {
  useCreateCompanyUserMutation,
  useGetCompanyManagersQuery,
} from 'src/api/redux';
import { Dialog, PulseButton, Checkbox, Input } from 'src/components/PulseUI';
import { useAuthorization } from 'src/hooks/authorization';
import { RolesDropdown } from './components';

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
}

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  is_manager: boolean;
  managers: string[];
  is_lir_eligible: boolean;
  /**
   * Legacy flag, no longer editable here — the permission model covers it. Kept
   * in the payload because the create endpoint reads the key unconditionally.
   */
  is_integration_admin: boolean;
  invite_now: boolean;
  phone_number: string;
  /** Role *code* to assign on create — the API resolves `roles` by code. */
  role: string;
}

const getInitialValues = (): FormValues => ({
  first_name: '',
  last_name: '',
  email: '',
  is_manager: false,
  managers: [],
  is_lir_eligible: false,
  is_integration_admin: false,
  invite_now: false,
  phone_number: '',
  role: '',
});

const AddUserDialog = ({ onClose }: AddUserDialogProps) => {
  const log = useLogging();
  const { setNotification } = useNotification();
  const { showAddEditUserModal } = useDashboard();
  const { userManagementCapabilities } = useAuthorization();
  const { isStaff } = userManagementCapabilities;

  // Only companyId is needed — this dialog is add-only.
  // userId is intentionally not read from params: the dialog has no edit path
  // in the UI, and reading userId was causing it to silently load user data
  // while the dialog was closed (when visiting a user profile page).
  const { companyId } = useParams<{ companyId: string }>();

  const { data: managers, refetch: refetchManagers } =
    useGetCompanyManagersQuery({ companyId }, { skip: !companyId });

  const [createCompanyUser] = useCreateCompanyUserMutation();

  const sortedManagers = useMemo(() => {
    if (!managers?.results) return;
    return [...managers.results].sort((a, b) => {
      if (a.first_name < b.first_name) return -1;
      if (a.first_name > b.first_name) return 1;
      return 0;
    });
  }, [managers]);

  const [isLoading, setIsLoading] = useState(false);
  const [formValues, setFormValues] = useState<FormValues>(getInitialValues());
  const [initialValues, setInitialValues] = useState<FormValues>(
    getInitialValues()
  );

  const isDirty = JSON.stringify(formValues) !== JSON.stringify(initialValues);
  const isValid =
    formValues.first_name.trim() !== '' &&
    formValues.last_name.trim() !== '' &&
    formValues.email.trim() !== '';

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { name, value } = e.target;
      setFormValues((prev) => ({ ...prev, [name]: value }));
    },
    []
  );

  const handleCheckboxChange = useCallback(
    (field: keyof FormValues) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: e.target.checked }));
    },
    []
  );

  const handleRoleChange = useCallback((value: string) => {
    setFormValues((prev) => ({ ...prev, role: value }));
  }, []);

  const handleManagerToggle = useCallback(
    (managerId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormValues((prev) => ({
        ...prev,
        managers: e.target.checked
          ? [...prev.managers, managerId]
          : prev.managers.filter((id) => id !== managerId),
      }));
    },
    []
  );

  const resetForm = useCallback(() => {
    const defaults = getInitialValues();
    setFormValues(defaults);
    setInitialValues(defaults);
    setIsLoading(false);
  }, []);

  const handleCancel = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { role, ...userData } = formValues;

      // The create endpoint accepts `roles` directly since MKT-429, so the role
      // is assigned in the same request rather than by a follow-up bulk-assign
      // call. Omitted entirely when nothing was picked.
      await createCompanyUser({
        companyId,
        ...userData,
        ...(role ? { roles: [role] } : {}),
      }).unwrap();

      log.event('User created', {
        source: 'AddUserDialog',
        companyId,
        data: formValues,
      });
      setNotification({
        title: 'Success',
        message: 'User created',
        type: 'success',
      });

      refetchManagers();
      resetForm();
      onClose();
    } catch (err) {
      log.exception('Error creating user', err);
      setNotification({
        title: 'Error',
        message: err?.error_message || 'Error creating user',
        type: 'error',
      });
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      isOpen={showAddEditUserModal}
      onClose={onClose}
      size="lg"
      className="!h-auto !max-w-lg"
      showCloseButton
      data-testid="add-user-dialog-sheet"
    >
      <Dialog.Header>
        <h2
          className="text-lg font-semibold"
          data-testid="add-user-dialog-title"
        >
          Add User
        </h2>
      </Dialog.Header>
      <form onSubmit={handleSubmit} data-testid="add-user-form">
        <Dialog.Content className="p-6">
          <div className="flex gap-4 mb-4">
            <div className="flex-1">
              <label
                className="text-sm font-semibold text-neutral-700 mb-2 block"
                data-testid="first-name-label"
              >
                First name
              </label>
              <Input
                name="first_name"
                placeholder="First name"
                value={formValues.first_name}
                onChange={handleInputChange}
                fullWidth
                data-testid="first-name-input"
              />
            </div>
            <div className="flex-1">
              <label
                className="text-sm font-semibold text-neutral-700 mb-2 block"
                data-testid="last-name-label"
              >
                Last name
              </label>
              <Input
                name="last_name"
                placeholder="Last name"
                value={formValues.last_name}
                onChange={handleInputChange}
                fullWidth
                data-testid="last-name-input"
              />
            </div>
          </div>
          <div className="flex flex-col mb-4">
            <label
              className="text-sm font-semibold text-neutral-700 mb-2 block"
              data-testid="email-label"
            >
              Email
            </label>
            <Input
              name="email"
              type="email"
              placeholder="Email"
              value={formValues.email}
              onChange={handleInputChange}
              fullWidth
              data-testid="email-input"
            />
          </div>
          {/* Phone number is unused by the product — MedScout-only (MKT-382) */}
          {isStaff && (
            <div className="flex flex-col mb-4">
              <label
                className="text-sm font-semibold text-neutral-700 mb-2 block"
                data-testid="phone-number-label"
              >
                Phone Number
              </label>
              <Input
                name="phone_number"
                placeholder="Phone Number"
                value={formValues.phone_number}
                onChange={handleInputChange}
                fullWidth
                data-testid="phone-number-input"
              />
            </div>
          )}
          <div className="flex flex-col mb-4">
            <label
              className="text-sm font-semibold text-neutral-700 mb-2 block"
              data-testid="role-label"
            >
              Role
            </label>
            <RolesDropdown
              value={formValues.role}
              onChange={handleRoleChange}
            />
          </div>
          <div className="flex flex-col mb-4">
            <label
              className="text-sm font-semibold text-neutral-700 mb-2 block"
              data-testid="managers-label"
            >
              Managers
            </label>
            <div
              className="h-40 max-h-full overflow-hidden overflow-y-auto border border-neutral-300 rounded-lg p-2"
              aria-label="managers"
              data-testid="managers-list"
            >
              {sortedManagers?.map((user, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between py-1"
                  data-testid={`manager-item-${index}`}
                >
                  <span
                    className="text-sm text-neutral-700"
                    data-testid={`manager-name-${index}`}
                  >
                    {`${user.first_name} ${user.last_name}`}
                  </span>
                  <Checkbox
                    size="sm"
                    checked={formValues.managers.includes(String(user.id))}
                    onChange={handleManagerToggle(String(user.id))}
                    data-testid={`manager-checkbox-${index}`}
                  />
                </div>
              ))}
            </div>
          </div>
          <div
            className="grid grid-cols-2 gap-2 border border-neutral-300 rounded-lg p-3"
            data-testid="permissions-list"
          >
            <Checkbox
              size="sm"
              label="Is Manager"
              checked={formValues.is_manager}
              onChange={handleCheckboxChange('is_manager')}
              data-testid="is-manager-checkbox"
            />
            {/* MedScout-internal reporting flag (MKT-382) */}
            {isStaff && (
              <Checkbox
                size="sm"
                label="LIR Eligible"
                checked={formValues.is_lir_eligible}
                onChange={handleCheckboxChange('is_lir_eligible')}
                data-testid="is-lir-eligible-checkbox"
              />
            )}
            <Checkbox
              size="sm"
              label="Invite now"
              checked={formValues.invite_now}
              onChange={handleCheckboxChange('invite_now')}
              data-testid="invite-now-checkbox"
            />
          </div>
        </Dialog.Content>
        <Dialog.Actions data-testid="dialog-actions">
          <PulseButton
            variant="outline"
            color="neutral"
            onClick={handleCancel}
            aria-label="Cancel"
            data-testid="cancel-button"
          >
            Cancel
          </PulseButton>
          <PulseButton
            loading={isLoading}
            type="submit"
            variant="solid"
            color="primary"
            disabled={!isDirty || !isValid}
            aria-label="Create User"
            data-testid="submit-button"
          >
            Create User
          </PulseButton>
        </Dialog.Actions>
      </form>
    </Dialog>
  );
};

export default AddUserDialog;
